import { Text } from '@chakra-ui/react'
import qs from 'query-string'
import { drop } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import iconCloseXDark900 from '~/assets/icons-light-theme/icon-close-x-dark900.svg'
import iconSnippetDark900 from '~/assets/icons-light-theme/icon-snippet-dark900.svg'
import iconTrashCanDark700 from '~/assets/icons-light-theme/icon-trashCan-dark700.svg'
import iconTrashCanDark900 from '~/assets/icons-light-theme/icon-trashCan-dark900.svg'
import iconCloseRed from '~/assets/icons/icon-close-red.png'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import CharacterCount from '~/components/CharacterCounter'
import { ImageButton, Input } from '~/components/core'
import { ImageIcon } from '~/components/ImageIcon'
import InfiniteList from '~/components/InfiniteList'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { SnippetOptions } from '~/constants/config'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import { useGetPbxList } from '~/queries/pbxQueries'
import {
  MessageSnippetUpsertRequest,
  useDeleteSnippet,
  useGetSnippets,
  useSaveSnippet,
} from '~/queries/snippetQueries'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'

import {
  ApiError,
  MessageSnippet,
  MessageSnippetPostResponse,
  Pbx,
} from '@mangovoice/types'

import {
  CancelButtonWrapper,
  EditBodyInputArea,
  EditContentWrapper,
  EditFooterWrapper,
  EditTitleWrapper,
  EmptyStateWrapper,
  ErrorWrapper,
  FilterMenuWrapper,
  GroupDropdownWrapper,
  HeaderActionWrapper,
  HeaderTitleWrapper,
  HeaderWrapper,
  HelperText,
  ItemActionWrapper,
  ItemNameWrapper,
  ItemWrapper,
  ListWrapper,
  Loading,
  LoadingWrapper,
  RootWrapper,
  ShortcutNameInputWrapper,
  SnippetDropdown,
  SnippetLabel,
  TextAreaWrapper,
} from './SnippetSettingsStyles'
import FilterMenu from './SnippetsFilter'

interface SnippetForm {
  id?: number | undefined
  name: string
  pbxId?: number | null
  body: string
}
interface PbxGroupOption {
  value: number | null
  label: string
}
const { ErrorTextColor, BackgroundColor } =
  themeColorSectionKeys.Settings.SnippetSettings

export const SnippetsSettings = () => {
  const emptySnippet: SnippetForm = {
    name: '',
    body: '',
  }
  const initialGroupOption = useMemo(
    () => ({ value: null, label: 'Personal' }),
    [],
  )

  const [hasNextPage, setHasNextPage] = useState(false)
  const [nextPageOffset, setNextPageOffset] = useState('0')
  const [snippets, setSnippets] = useState<MessageSnippet[]>([])
  const [filter, setFilter] = useState('pbx')
  const [isEditOrNew, setIsEditOrNew] = useState(false)
  const [snippetForm, setSnippetForm] = useState<SnippetForm>(emptySnippet)
  const { pbxId: currentPbxId } = useUserState()
  const [error, setError] = useState<string | null>(null)
  const [pbxList, setPbxList] = useState<Pbx[] | null>([])
  const [groupOptions, setGroupOptions] = useState<PbxGroupOption[]>([
    initialGroupOption,
  ])

  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()

  const { refetch, isLoading, isFetching } = useGetSnippets(
    currentPbxId,
    nextPageOffset,
    filter === 'personal',
    (data) => {
      setSnippets([...snippets, ...data.results])
      setHasNextPage(data.next !== null)
      if (data.next) {
        const {
          query: { offset },
        } = qs.parseUrl(data.next)

        if (offset && typeof offset === 'string') setNextPageOffset(offset)
      }
    },
    {
      refetchOnWindowFocus: false,
    },
  )

  const handleSnippetDeleted = (data, snippetId) => {
    window.gtag('event', 'delete_snippet_success', {})
    const newSnippets = snippets.filter((snippet) => snippet.id !== snippetId)
    setSnippets(newSnippets)
  }

  const handleCancelEdit = () => {
    setIsEditOrNew(false)
    setSnippetForm(emptySnippet)
  }

  const setErrorWithTimeout = (e) => {
    setError(e)
    setTimeout(() => {
      setError(null)
    }, 7000)
  }

  const handleSnippetSaved = (response?: MessageSnippetPostResponse) => {
    if (response) {
      window.gtag('event', 'create_snippet_success', {})
      if (
        (filter === 'personal' && response.pbx_id === null) ||
        (filter === 'pbx' && response.pbx_id === currentPbxId)
      ) {
        let newSnippets: MessageSnippet[] = []
        const existingSnippet = snippets.find(
          (snippet) => snippet.id === response.id,
        )

        if (existingSnippet) {
          newSnippets = snippets.map((snippet) => {
            if (snippet.id === response.id) {
              return {
                id: response.id,
                name: response.name,
                body: response.body,
                pbx: response.pbx_id
                  ? { id: response.pbx_id, name: '' }
                  : undefined,
                scope: response.scope,
                created_at: response.created_at,
                updated_at: response.updated_at,
                created_by: {
                  full_name: '',
                  id: 0,
                },
                last_updated_by: {
                  full_name: '',
                  id: 0,
                },
              }
            }
            return snippet
          })
        } else {
          newSnippets = [...snippets]
          newSnippets.unshift({
            id: response.id,
            name: response.name,
            body: response.body,
            pbx: response.pbx_id
              ? { id: response.pbx_id, name: '' }
              : undefined,
            scope: response.scope,
            created_at: response.created_at,
            updated_at: response.updated_at,
            created_by: {
              full_name: '',
              id: 0,
            },
            last_updated_by: {
              full_name: '',
              id: 0,
            },
          })
        }
        setSnippets(newSnippets)
      } else if (response.pbx_id !== currentPbxId && filter === 'pbx') {
        const newSnippets = snippets.filter(
          (snippet) => snippet.pbx?.id === currentPbxId,
        )
        setSnippets(newSnippets)
      }
    }
  }
  const handleSnippetSaveFailed = (errorResponse: ApiError) => {
    window.gtag('event', 'create_snippet_failure', {
      error: errorResponse.message,
    })
    setErrorWithTimeout(
      errorResponse.message ?? 'Something went wrong. Please try again later.',
    )
  }

  const deleteMutation = useDeleteSnippet(handleSnippetDeleted)
  const saveMutation = useSaveSnippet(
    handleSnippetSaved,
    handleSnippetSaveFailed,
  )

  const handleSaveSnippet = () => {
    const { id, body, name, pbxId } = snippetForm

    const payload: MessageSnippetUpsertRequest = {
      name: `#${name}`,
      body,
      pbx_id: pbxId ?? null,
      message_group_id: null,
      id: id,
    }

    saveMutation.mutate(payload)
    handleCancelEdit()
  }

  useGetPbxList({
    onQuerySuccess: (data) => {
      setPbxList(data.results)
    },
  })

  const { count, hasExceededMaxCount } = useMaxCharacterCount(
    snippetForm.body.length,
  )

  useEffect(() => {
    if (pbxList) {
      setGroupOptions(() => {
        const pbxGroups = pbxList.map(({ id, name }) => ({
          value: id,
          label: name,
        }))

        return [{ ...initialGroupOption }, ...pbxGroups]
      })
    }
  }, [initialGroupOption, pbxList])

  const handleAddNew = () => {
    window.gtag('event', 'snippet_create_clicked', {})
    setIsEditOrNew(true)
    setSnippetForm(emptySnippet)
  }

  const handleChangeSnippetName = (name) => {
    setSnippetForm((prevValues) => ({ ...prevValues, name }))
  }

  const handleChangeSnippetBody = (e) => {
    const str = e.target.value as string
    setSnippetForm((prevValues) => ({
      ...prevValues,
      body: str.replace(/(<([^>]+)>)/gi, ''),
    }))
  }

  const handleSelectedGroup = (selectedValue) => {
    setSnippetForm((prevValues) => ({
      ...prevValues,
      pbxId: selectedValue === 'Personal' ? null : selectedValue,
    }))
  }

  const handleEditSnippet = (snippet: MessageSnippet) => {
    const { id, pbx, name, body } = snippet
    const cleanName = drop(1, name)

    setSnippetForm({
      id,
      body,
      name: cleanName,
      pbxId: pbx?.id ?? null,
    })

    setIsEditOrNew(true)
  }

  const handleDeleteSnippet = (e) => {
    setConfirmModalState({
      title: 'Are you sure?',
      message: 'Deleting this Snippet is permanent and cannot be undone',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDestructive: true,
      onConfirm: () => deleteMutation.mutate(e.id),
    })
  }

  const handleSelectedFilter = (selectedOption: string) => {
    if (selectedOption !== filter) {
      setFilter(selectedOption)
      setNextPageOffset('0')
      setSnippets([])
    }
  }

  const { name, body, pbxId } = snippetForm

  const selectedOption =
    groupOptions.find((option) => {
      return option.value === snippetForm.pbxId
    }) ?? initialGroupOption

  const showEditing = isEditOrNew
  const showLoading =
    !showEditing && (isLoading || (isFetching && snippets.length === 0))
  const showList = !showEditing && !showLoading && snippets.length > 0
  const showEmpty = !showEditing && !showLoading && !showList

  return (
    <RootWrapper>
      {error && (
        <ErrorWrapper>
          <Text textStyle={textStyleKeys.body2Semibold} color={ErrorTextColor}>
            {error.toString()}
            {error}
          </Text>
          <ImageIcon
            cursor
            width="12"
            height="12"
            icon={iconCloseRed}
            onClick={() => setError(null)}
          />
        </ErrorWrapper>
      )}
      <HeaderWrapper>
        <HeaderTitleWrapper>
          {showEditing ? (
            <EditTitleWrapper>
              <Text textStyle={textStyleKeys.Body2}>
                {snippetForm.id ? 'Edit Snippet' : 'New Snippet'}
              </Text>
            </EditTitleWrapper>
          ) : (
            <>
              <ImageIcon width="24" height="24" icon={iconSnippetDark900} />
              <Text textStyle={textStyleKeys.Body2}>Snippets</Text>
            </>
          )}
        </HeaderTitleWrapper>
        <HeaderActionWrapper>
          {showEditing ? (
            <ImageButton
              icon={iconCloseXDark900}
              width="33px"
              height="44px"
              imgWidth={16}
              imgHeight={16}
              backColor={'transparent'}
              activeColor={'transparent'}
              hoveredColor={'transparent'}
              disabledColor={'transparent'}
              onClick={() => handleCancelEdit()}
            />
          ) : (
            <MangoButton onClick={() => handleAddNew()} size={Size.sm}>
              New
            </MangoButton>
          )}
        </HeaderActionWrapper>
      </HeaderWrapper>
      {!showEditing && (
        <FilterMenuWrapper>
          <FilterMenu
            selectedFilter={filter}
            options={SnippetOptions()}
            onChange={handleSelectedFilter}
          />
        </FilterMenuWrapper>
      )}
      <div style={{ height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
        <ListWrapper>
          {showList && (
            <InfiniteList
              style={{ backgroundColor: BackgroundColor }}
              data={snippets}
              onEndReached={() => (hasNextPage ? refetch() : null)}
              listItem={(_, snippet) => {
                return (
                  <ItemWrapper key={`snippet-${snippet.id}`}>
                    <ItemNameWrapper onClick={() => handleEditSnippet(snippet)}>
                      <Text textStyle={textStyleKeys.Body4}>
                        {snippet.name}
                      </Text>
                    </ItemNameWrapper>
                    <ItemActionWrapper>
                      <ImageButton
                        icon={iconTrashCanDark700}
                        hoverIcon={iconTrashCanDark900}
                        activeIcon={iconTrashCanDark900}
                        width="26px"
                        height="34px"
                        imgWidth={14}
                        imgHeight={14}
                        tooltipText="Delete"
                        backColor={'transparent'}
                        activeColor={'transparent'}
                        hoveredColor={'transparent'}
                        disabledColor={'transparent'}
                        onClick={() => handleDeleteSnippet(snippet)}
                      />
                    </ItemActionWrapper>
                  </ItemWrapper>
                )
              }}
            />
          )}
          {showEditing && (
            <>
              <EditContentWrapper paddingBottom="1rem">
                <ShortcutNameInputWrapper>
                  <SnippetLabel>
                    <Text textStyle={textStyleKeys.Body4_Bold}>Shortcut</Text>
                  </SnippetLabel>
                  <Input
                    // @ts-expect-error TODO: fix this
                    prefix="#"
                    placeholder="GoodMorning"
                    noBorder
                    value={name}
                    onChange={handleChangeSnippetName}
                  />
                </ShortcutNameInputWrapper>
                <HelperText>
                  <Text textStyle={textStyleKeys.Body4}>
                    One word, no spaces. e.g.: #good-morning or #TestResults
                  </Text>
                </HelperText>
                <GroupDropdownWrapper>
                  <SnippetLabel alignItems={'flex-start'}>
                    <Text textStyle={textStyleKeys.Body4_Bold} textAlign="left">
                      Visibility
                    </Text>
                  </SnippetLabel>
                  <SnippetDropdown
                    options={groupOptions}
                    selected={pbxId}
                    showText={selectedOption.label}
                    onSelect={handleSelectedGroup}
                  />
                </GroupDropdownWrapper>
                <HelperText>
                  <Text textStyle={textStyleKeys.Body4}>
                    Personal = only you can access it, Company = everyone will
                    access it
                  </Text>
                </HelperText>
                <SnippetLabel>
                  <Text textStyle={textStyleKeys.Body4_Bold}>Body</Text>
                </SnippetLabel>
                <TextAreaWrapper>
                  <EditBodyInputArea
                    hasExceededMaxCount={hasExceededMaxCount}
                    placeholder="Write a canned message here. You can add emails, web links, phone numbers..."
                    value={body}
                    onChange={handleChangeSnippetBody}
                  />
                  <CharacterCount count={count} />
                </TextAreaWrapper>
              </EditContentWrapper>
              <EditFooterWrapper marginTop="1rem">
                <CancelButtonWrapper>
                  <MangoButton
                    size={Size.sm}
                    variant={MangoButtonVariant.Default_200}
                    onClick={() => handleCancelEdit()}
                  >
                    Cancel
                  </MangoButton>
                </CancelButtonWrapper>
                <MangoButton
                  disabled={name === '' || body === '' || hasExceededMaxCount}
                  onClick={() => handleSaveSnippet()}
                  size={Size.sm}
                >
                  Save
                </MangoButton>
              </EditFooterWrapper>
            </>
          )}
          {showLoading && (
            <LoadingWrapper
              style={{
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <Loading>Loading Snippets...</Loading>
            </LoadingWrapper>
          )}
          {showEmpty && (
            <EmptyStateWrapper>
              <Text textStyle={textStyleKeys.Body1}>No Snippets Found</Text>
            </EmptyStateWrapper>
          )}
        </ListWrapper>
      </div>
    </RootWrapper>
  )
}

export default SnippetsSettings
